import { lazy, Suspense, useState, type ReactNode } from "react";

import { BotaoTema } from "../app/BotaoTema";
import { Marca } from "../app/Marca";
import { useLargo, useMuitoLargo } from "./largura";
import estilos from "./mesa.module.css";

// Trilho e inspetor carregam sob demanda: no celular só quando a pessoa abre, e nunca antes do
// card (orçamento do ADR 0010).
const Trilho = lazy(() => import("./Trilho"));
// A folha (inspetor no celular) e a gaveta (trilho no celular) só carregam ao abrir.
const FolhaTardia = lazy(() => import("./Folha").then((m) => ({ default: m.Folha })));

function Folha(props: Parameters<typeof FolhaTardia>[0]) {
  if (!props.aberta) return null;
  return (
    <Suspense fallback={null}>
      <FolhaTardia {...props} />
    </Suspense>
  );
}

/**
 * A mesa com inspetor (docs/specs/2026-10-19-ux-mesa-com-inspetor.md): trilho com o histórico à
 * esquerda, o fio da análise no centro com a caixa de chat embaixo e o inspetor à direita. No
 * celular, o trilho vira gaveta (☰); abaixo da tela larga, o inspetor é uma folha que sobe quando a
 * pessoa pede.
 */
export function Mesa(props: {
  /** O fim da migalha: "Nova" ou a alegação. */
  migalha: string;
  /** Link privado da análise aberta, para marcá-la no histórico. */
  atual?: string | undefined;
  children: ReactNode;
  /** A caixa de chat presa embaixo (na mesa vazia ela vai no meio, dentro de `children`). */
  doca?: ReactNode;
  /** O inspetor; `naFolha`: no celular, a folha já tem título e botão de fechar. */
  inspetor: (naFolha: boolean) => ReactNode;
  /** No celular, a folha do inspetor só abre quando a pessoa pede a evidência de um bloco. */
  inspetorAberto: boolean;
  tituloInspetor: string;
  aoFecharInspetor: () => void;
}) {
  const largo = useLargo();
  const muitoLargo = useMuitoLargo();
  const [gaveta, setGaveta] = useState(false);
  return (
    <div className={estilos.mesa}>
      {largo && (
        <nav aria-label="Histórico" className={estilos.trilho}>
          <div className={estilos.trilhoTopo}>
            <Marca />
            <BotaoTema />
          </div>
          <Suspense fallback={null}>
            <Trilho atual={props.atual} />
          </Suspense>
        </nav>
      )}
      <div className={estilos.centro}>
        <div className={estilos.barraTopo}>
          {!largo && (
            <button
              type="button"
              className={estilos.botaoTrilho}
              aria-haspopup="dialog"
              onClick={() => {
                setGaveta(true);
              }}
            >
              <span aria-hidden="true">☰</span>
              <span className="visualmente-oculto">Histórico</span>
            </button>
          )}
          <p className={estilos.migalha}>
            Análises <span aria-hidden="true">/</span> <span>{props.migalha}</span>
          </p>
          {!largo && <BotaoTema />}
        </div>
        <div className={estilos.fio}>{props.children}</div>
        {props.doca && <div className={estilos.doca}>{props.doca}</div>}
      </div>
      {muitoLargo ? (
        // The inspector scrolls on its own; keyboard users need to reach it to scroll it
        // (axe: scrollable-region-focusable).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <aside aria-label="Inspetor" className={estilos.inspetor} tabIndex={0}>
          {props.inspetor(false)}
        </aside>
      ) : (
        <Folha
          aberta={props.inspetorAberto}
          titulo={props.tituloInspetor}
          aoFechar={props.aoFecharInspetor}
        >
          {props.inspetor(true)}
        </Folha>
      )}
      {!largo && (
        <Folha
          aberta={gaveta}
          titulo="Histórico"
          lado="esquerda"
          tituloOculto
          aoFechar={() => {
            setGaveta(false);
          }}
        >
          <nav aria-label="Histórico" className={estilos.trilhoGaveta}>
            <Marca
              onClick={() => {
                setGaveta(false);
              }}
            />
            <Suspense fallback={<p role="status">Carregando…</p>}>
              <Trilho
                atual={props.atual}
                aoNavegar={() => {
                  setGaveta(false);
                }}
              />
            </Suspense>
          </nav>
        </Folha>
      )}
    </div>
  );
}
